import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LogIn, LogOut, Clock, CheckCircle, ArrowLeft, UserCircle, MapPin } from 'lucide-react';
import { checkGeofence } from '@/lib/geofence';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = ["Food pantry", "Soup Kitchen", "SVdP", "Canterbury House", "Parish Gardening", "Parish-other"];

export default function KioskPage() {
  const [step, setStep] = useState('id'); // id, pin-checkin, pin-checkout, success
  const [volunteerId, setVolunteerId] = useState('');
  const [pin, setPin] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [volunteerName, setVolunteerName] = useState('');
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [action, setAction] = useState(''); // checkin or checkout

  // Auto-reset after success
  useEffect(() => {
    if (step === 'success') {
      const timer = setTimeout(() => {
        resetAll();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const resetAll = () => {
    setStep('id');
    setVolunteerId('');
    setPin('');
    setServiceType('');
    setVolunteerName('');
    setError('');
    setResult(null);
    setAction('');
  };

  const handleLookup = async (selectedAction) => {
    if (!volunteerId.trim()) { setError('Enter your Volunteer ID'); return; }
    setError('');
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/kiosk/lookup`, { volunteer_id: volunteerId });
      setVolunteerName(data.name);
      setAction(selectedAction);
      setStep(selectedAction === 'checkin' ? 'pin-checkin' : 'pin-checkout');
    } catch (e) {
      setError(e.response?.data?.detail || 'Volunteer ID not found');
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckin = async () => {
    if (!pin || pin.length < 4) { setError('Enter your PIN (4-6 digits)'); return; }
    if (!serviceType) { setError('Select a service type'); return; }
    setError('');
    setProcessing(true);
    try {
      const geo = await checkGeofence();
      if (!geo.allowed) { setError(geo.message); setProcessing(false); return; }
      const { data } = await axios.post(`${API}/kiosk/checkin`, {
        volunteer_id: volunteerId, pin, service_type: serviceType
      });
      setResult(data);
      setStep('success');
    } catch (e) {
      setError(e.response?.data?.detail || 'Check-in failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckout = async () => {
    if (!pin || pin.length < 4) { setError('Enter your PIN (4-6 digits)'); return; }
    setError('');
    setProcessing(true);
    try {
      const geo = await checkGeofence();
      if (!geo.allowed) { setError(geo.message); setProcessing(false); return; }
      const { data } = await axios.post(`${API}/kiosk/checkout`, { volunteer_id: volunteerId, pin });
      setResult(data);
      setStep('success');
    } catch (e) {
      setError(e.response?.data?.detail || 'Check-out failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex flex-col" data-testid="kiosk-page">
      {/* Header */}
      <div className="bg-[#3F6352] text-white p-6 text-center">
        <img src="/svdp-logo.png" alt="SVdP Chicago" className="w-14 h-14 mx-auto mb-3 rounded-full shadow-lg" />
        <h1 className="font-['Manrope'] text-2xl md:text-3xl font-bold">Community Service Tracker</h1>
        <p className="text-sm md:text-base opacity-90 mt-1">Volunteer Check-in / Check-out</p>
        <p className="text-xs opacity-70 mt-1">Powered by SVdP &bull; Mary, Mother of God Outreach Ministries</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">

          {/* Step: Enter Volunteer ID */}
          {step === 'id' && (
            <div className="animate-fade-in space-y-6" data-testid="kiosk-id-step">
              <div className="text-center mb-8">
                <UserCircle className="h-16 w-16 text-[#D3735A] mx-auto mb-4" />
                <h2 className="font-['Manrope'] text-2xl font-bold text-[#2D2B2A]">Welcome, Volunteer!</h2>
                <p className="text-[#5C5855] mt-2">Enter your Volunteer ID to check in or out</p>
              </div>

              {error && (
                <div className="p-4 bg-[#B74134]/10 border border-[#B74134]/20 rounded-xl text-sm text-[#B74134] text-center" data-testid="kiosk-error">{error}</div>
              )}

              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Volunteer ID</Label>
                <Input
                  data-testid="kiosk-volunteer-id"
                  type="text"
                  value={volunteerId}
                  onChange={(e) => setVolunteerId(e.target.value.toUpperCase())}
                  placeholder="VOL-XXXXXX"
                  className="text-center text-lg h-14 bg-white border-[#D1C9C0] rounded-xl font-mono tracking-wider"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Button
                  data-testid="kiosk-checkin-btn"
                  onClick={() => handleLookup('checkin')}
                  disabled={processing}
                  className="bg-[#3F6352] hover:bg-[#2C4A3C] text-white rounded-xl h-14 text-base font-semibold"
                >
                  <LogIn className="h-5 w-5 mr-2" /> Check In
                </Button>
                <Button
                  data-testid="kiosk-checkout-btn"
                  onClick={() => handleLookup('checkout')}
                  disabled={processing}
                  className="bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-xl h-14 text-base font-semibold"
                >
                  <LogOut className="h-5 w-5 mr-2" /> Check Out
                </Button>
              </div>

              <a href="/login" className="block text-center text-sm text-[#5C5855] hover:text-[#D3735A] mt-4" data-testid="kiosk-to-login">
                <ArrowLeft className="h-3 w-3 inline mr-1" /> Back to full login
              </a>
            </div>
          )}

          {/* Step: PIN + Service Type for Check-in */}
          {step === 'pin-checkin' && (
            <div className="animate-fade-in space-y-6" data-testid="kiosk-checkin-step">
              <div className="text-center mb-6">
                <h2 className="font-['Manrope'] text-2xl font-bold text-[#2D2B2A]">Hi, {volunteerName}!</h2>
                <p className="text-[#5C5855] mt-1">Enter your PIN and select your service</p>
              </div>

              {error && (
                <div className="p-4 bg-[#B74134]/10 border border-[#B74134]/20 rounded-xl text-sm text-[#B74134] text-center">{error}</div>
              )}

              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">PIN</Label>
                <Input
                  data-testid="kiosk-pin-input"
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter PIN"
                  className="text-center text-xl h-14 bg-white border-[#D1C9C0] rounded-xl tracking-[0.3em]"
                  inputMode="numeric"
                  autoFocus
                />
              </div>

              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Service Type</Label>
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger data-testid="kiosk-service-select" className="h-14 border-[#D1C9C0] bg-white rounded-xl text-base">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {SERVICE_TYPES.map((st) => (
                      <SelectItem key={st} value={st} className="text-base py-3">{st}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button data-testid="kiosk-confirm-checkin" onClick={handleCheckin} disabled={processing}
                className="w-full bg-[#3F6352] hover:bg-[#2C4A3C] text-white rounded-xl h-14 text-base font-semibold">
                {processing ? 'Checking in...' : <><Clock className="h-5 w-5 mr-2" /> Confirm Check In</>}
              </Button>

              <Button variant="ghost" onClick={resetAll} className="w-full text-[#5C5855]">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            </div>
          )}

          {/* Step: PIN for Check-out */}
          {step === 'pin-checkout' && (
            <div className="animate-fade-in space-y-6" data-testid="kiosk-checkout-step">
              <div className="text-center mb-6">
                <h2 className="font-['Manrope'] text-2xl font-bold text-[#2D2B2A]">Hi, {volunteerName}!</h2>
                <p className="text-[#5C5855] mt-1">Enter your PIN to check out</p>
              </div>

              {error && (
                <div className="p-4 bg-[#B74134]/10 border border-[#B74134]/20 rounded-xl text-sm text-[#B74134] text-center">{error}</div>
              )}

              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">PIN</Label>
                <Input
                  data-testid="kiosk-pin-checkout"
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter PIN"
                  className="text-center text-xl h-14 bg-white border-[#D1C9C0] rounded-xl tracking-[0.3em]"
                  inputMode="numeric"
                  autoFocus
                />
              </div>

              <Button data-testid="kiosk-confirm-checkout" onClick={handleCheckout} disabled={processing}
                className="w-full bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-xl h-14 text-base font-semibold">
                {processing ? 'Checking out...' : <><LogOut className="h-5 w-5 mr-2" /> Confirm Check Out</>}
              </Button>

              <Button variant="ghost" onClick={resetAll} className="w-full text-[#5C5855]">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && result && (
            <div className="animate-fade-in text-center" data-testid="kiosk-success">
              <CheckCircle className="h-20 w-20 text-[#2C5545] mx-auto mb-6" />
              <h2 className="font-['Manrope'] text-2xl font-bold text-[#2D2B2A] mb-2">
                {action === 'checkin' ? 'Checked In!' : 'Checked Out!'}
              </h2>
              <p className="text-lg text-[#5C5855] mb-2">{result.message}</p>
              {result.hours && (
                <p className="text-3xl font-bold text-[#3F6352] font-['Manrope'] mt-4">{result.hours} hours</p>
              )}
              <p className="text-sm text-[#8A847F] mt-6">This screen will reset in a few seconds...</p>
              <Button onClick={resetAll} className="mt-6 bg-[#F2EFEB] hover:bg-[#E8E3DD] text-[#2D2B2A] border border-[#D1C9C0] rounded-xl">
                Done
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 text-center border-t border-[#E8E3DD]">
        <p className="text-xs text-[#8A847F] flex items-center justify-center gap-1">
          <MapPin className="h-3 w-3" /> 4827 N. Kenmore Ave, Chicago — Location verification required for check-in
        </p>
      </div>
    </div>
  );
}
